import { useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { CheckCircle, XCircle, Clock, FirstAid, Receipt, Airplane, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";
import { InvoiceDialog } from "./InvoiceDialog";
import { dayRange, money, monthLabel, ukDate, ukTime, StatusPill } from "./hrUtils";

const Panel = ({ icon: Icon, title, count, children, testId }) => (
  <section className="rounded-xl border border-border bg-card" data-testid={testId}>
    <header className="flex items-center gap-2 px-4 py-3 border-b border-border">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <h3 className="text-sm font-semibold">{title}</h3>
      {count > 0 && <span className="ml-auto text-xs font-semibold rounded-full bg-primary/10 text-primary px-2 py-0.5">{count}</span>}
    </header>
    <div className="divide-y divide-border">{children}</div>
  </section>
);
const Empty = ({ children }) => <p className="px-4 py-4 text-sm text-muted-foreground">{children}</p>;

/** Operations Director dashboard: what needs a decision and who is in / off today. */
export const OpsOverview = ({ data, reload }) => {
  const [notes, setNotes] = useState({});
  const [inv, setInv] = useState(null);
  const decide = async (l, action) => {
    try {
      await API.post(`/hr/leave/${l.id}/${action}`, { note: notes[l.id] || "" });
      toast.success(`Leave ${action === "approve" ? "approved" : "declined"} for ${l.user_name}`); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openInv = async (r) => { try { const { data: d } = await API.get(`/hr/invoices/${r.id}`); setInv(d); } catch (e) { toast.error(formatApiError(e)); } };
  if (!data) return null;
  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="hr-ops-overview">
      <Panel icon={Airplane} title="Leave awaiting your decision" count={data.pending_leave.length} testId="hr-ops-pending-leave">
        {data.pending_leave.length === 0 && <Empty>Nothing waiting.</Empty>}
        {data.pending_leave.map((l) => (
          <div key={l.id} className="px-4 py-3 space-y-2" data-testid={`hr-ops-leave-${l.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div><p className="text-sm font-medium">{l.user_name} <span className="text-muted-foreground font-normal">· {l.days}d</span></p><p className="text-xs text-muted-foreground">{dayRange(l.start_date, l.end_date)}{l.note ? ` — ${l.note}` : ""}</p></div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" onClick={() => decide(l, "approve")} data-testid={`hr-ops-approve-${l.id}`}><CheckCircle className="h-4 w-4 mr-1" /> Approve</Button>
                <Button size="sm" variant="outline" className="text-red-700" onClick={() => decide(l, "decline")} data-testid={`hr-ops-decline-${l.id}`}><XCircle className="h-4 w-4 mr-1" /> Decline</Button>
              </div>
            </div>
            <Input className="h-8 text-xs" placeholder="Note to them (optional)" value={notes[l.id] || ""} onChange={(e) => setNotes({ ...notes, [l.id]: e.target.value })} />
          </div>
        ))}
      </Panel>
      <Panel icon={Receipt} title="Invoices to review" count={data.invoices.length} testId="hr-ops-invoices">
        {data.invoices.length === 0 && <Empty>No submitted invoices.</Empty>}
        {data.invoices.map((r) => (
          <button key={r.id} className="w-full text-left px-4 py-3 flex items-center justify-between gap-2 hover:bg-muted/40" onClick={() => openInv(r)} data-testid={`hr-ops-invoice-${r.id}`}>
            <div><p className="text-sm font-medium">{r.user_name} · {r.invoice_number}</p><p className="text-xs text-muted-foreground">{monthLabel(r.month)}</p></div>
            <div className="flex items-center gap-2"><span className="font-semibold tabular-nums">{money(r.total)}</span><StatusPill status={r.status} /></div>
          </button>
        ))}
      </Panel>
      <Panel icon={Clock} title="In the office now" count={data.clocked_in.length} testId="hr-ops-clocked-in">
        {data.clocked_in.length === 0 && <Empty>Nobody is clocked in.</Empty>}
        {data.clocked_in.map((c) => (
          <Link key={c.user_id} to={`/hr?user_id=${c.user_id}`} className="px-4 py-2.5 flex items-center justify-between text-sm hover:bg-muted/40 block">
            <span className="font-medium">{c.user_name}</span><span className="text-muted-foreground text-xs">since {ukTime(c.clock_in)}</span>
          </Link>
        ))}
        {data.off_today.length > 0 && <p className="px-4 py-2 text-xs text-muted-foreground">Off today: {data.off_today.map((o) => o.user_name).join(", ")}</p>}
      </Panel>
      <Panel icon={FirstAid} title="Off sick" count={data.open_sickness.length} testId="hr-ops-sick">
        {data.open_sickness.length === 0 && <Empty>Nobody off sick.</Empty>}
        {data.open_sickness.map((s) => (
          <Link key={s.id} to={`/hr?user_id=${s.user_id}&tab=sickness`} className="px-4 py-2.5 flex items-center justify-between text-sm hover:bg-muted/40 block">
            <span><span className="font-medium">{s.user_name}</span> <span className="text-muted-foreground">since {ukDate(s.start_date)}</span></span><span className="text-xs text-muted-foreground truncate max-w-[40%]">{s.reason}</span>
          </Link>
        ))}
      </Panel>
      <Panel icon={CalendarBlank} title="Leave in the next 30 days" count={data.upcoming_leave.length} testId="hr-ops-upcoming">
        {data.upcoming_leave.length === 0 && <Empty>No leave coming up.</Empty>}
        {data.upcoming_leave.map((l, i) => (
          <div key={i} className="px-4 py-2.5 flex items-center justify-between text-sm"><span><span className="font-medium">{l.user_name}</span> <span className="text-muted-foreground">{dayRange(l.start_date, l.end_date)} · {l.days}d</span></span><StatusPill status={l.status} /></div>
        ))}
      </Panel>
      {inv && <InvoiceDialog invoice={inv} isOps isOwner={false} onClose={() => setInv(null)} onChanged={reload} />}
    </div>
  );
};
